import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { useData } from '../context/DataContext';
import SubscribeForm from '../components/SubscribeForm';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { blogs } = useData();
  const blog = blogs.find((b) => b.id === id);

  if (!blog) {
    return (
      <div className="blog-detail">
        <div className="empty-state">
          <h3>Post not found</h3>
          <p>This article doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let currentBlock = [];
    let inCodeBlock = false;
    let codeContent = '';

    lines.forEach((line, i) => {
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(<pre key={`code-${i}`}><code>{codeContent}</code></pre>);
          codeContent = '';
          inCodeBlock = false;
        } else {
          if (currentBlock.length) {
            elements.push(...flushBlock(currentBlock, i));
            currentBlock = [];
          }
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeContent += line + '\n';
        return;
      }

      // Standalone image: ![alt](url)
      const imageMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imageMatch) {
        if (currentBlock.length) { elements.push(...flushBlock(currentBlock, i)); currentBlock = []; }
        elements.push(
          <div key={`img-${i}`} className="blog-content-image">
            <img src={imageMatch[2]} alt={imageMatch[1]} loading="lazy" />
          </div>
        );
        return;
      }

      if (line.startsWith('## ')) {
        if (currentBlock.length) { elements.push(...flushBlock(currentBlock, i)); currentBlock = []; }
        elements.push(<h2 key={`h2-${i}`}>{line.replace('## ', '')}</h2>);
      } else if (line.startsWith('### ')) {
        if (currentBlock.length) { elements.push(...flushBlock(currentBlock, i)); currentBlock = []; }
        elements.push(<h3 key={`h3-${i}`}>{line.replace('### ', '')}</h3>);
      } else if (line.startsWith('- ')) {
        if (currentBlock.length && currentBlock[0].type !== 'li') {
          elements.push(...flushBlock(currentBlock, i));
          currentBlock = [];
        }
        currentBlock.push({ type: 'li', text: line.replace('- ', ''), key: i });
      } else if (/^\d+\.\s/.test(line)) {
        if (currentBlock.length && currentBlock[0].type !== 'oli') {
          elements.push(...flushBlock(currentBlock, i));
          currentBlock = [];
        }
        currentBlock.push({ type: 'oli', text: line.replace(/^\d+\.\s/, ''), key: i });
      } else if (line.trim()) {
        if (currentBlock.length && currentBlock[0].type !== 'p') {
          elements.push(...flushBlock(currentBlock, i));
          currentBlock = [];
        }
        currentBlock.push({ type: 'p', text: line, key: i });
      } else {
        if (currentBlock.length) { elements.push(...flushBlock(currentBlock, i)); currentBlock = []; }
      }
    });

    if (currentBlock.length) elements.push(...flushBlock(currentBlock, lines.length));
    return elements;
  };

  const flushBlock = (block, keyBase) => {
    if (!block.length) return [];
    if (block[0].type === 'li') {
      return [<ul key={`ul-${keyBase}`}>{block.map((item) => <li key={item.key}>{formatInline(item.text)}</li>)}</ul>];
    }
    if (block[0].type === 'oli') {
      return [<ol key={`ol-${keyBase}`}>{block.map((item) => <li key={item.key}>{formatInline(item.text)}</li>)}</ol>];
    }
    return block.map((item) => <p key={`p-${item.key}`}>{formatInline(item.text)}</p>);
  };

  const formatInline = (text) => {
    const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
    return parts.map((part, i) => {
      if (part.startsWith('`') && part.endsWith('`')) return <code key={i}>{part.slice(1, -1)}</code>;
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) return <a key={i} href={linkMatch[2]} target="_blank" rel="noopener noreferrer">{linkMatch[1]}</a>;
      return part;
    });
  };

  return (
    <div className="blog-detail">
      <div className="blog-detail-header">
        <button className="blog-detail-back" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>
        <span className="blog-card-category">{blog.category}</span>
        <h1>{blog.title}</h1>
        <div className="blog-detail-meta">
          <span>{blog.date}</span>
          <span>{blog.readTime}</span>
        </div>
      </div>

      {blog.cover && (
        <div className="blog-detail-cover">
          <img src={blog.cover} alt={blog.title} />
        </div>
      )}

      <div className="blog-detail-content">
        {renderContent(blog.content)}
      </div>

      <div className="blog-card-tags" style={{ marginTop: 32 }}>
        {blog.tags?.map((tag) => (
          <span key={tag} className="blog-card-tag">#{tag}</span>
        ))}
      </div>

      <SubscribeForm />
    </div>
  );
};

export default BlogDetail;
